import {useCallback} from '@wordpress/element';
import {registerExpressPaymentMethod} from '@woocommerce/blocks-registry';
import classnames from 'classnames';
import {getSettings, initStripe as loadStripe, canMakePayment, isCartPage, isCheckoutPage} from "../util";
import {Elements, useStripe} from "@stripe/react-stripe-js";
import ErrorBoundary from "../error-boundary";
import {
    usePaymentRequest,
    useProcessPaymentIntent,
    useExportedValues,
    useAfterProcessingPayment,
    useStripeError,
    useExpressBreakpointWidth, useProcessExpressCheckoutError
} from '../hooks';
import {ApplePayPreview} from "./preview";

const getData = getSettings('stripe_applepay_data');

const ApplePayContent = (props) => {
    return (
        <ErrorBoundary>
            <div className='wc-stripe-apple-pay-container'>
                <Elements stripe={loadStripe}>
                    <ApplePayButton {...props}/>
                </Elements>
            </div>
        </ErrorBoundary>
    );
}

const ApplePayButton = (
    {
        getData,
        onClick,
        onClose,
        onError,
        billing,
        shippingData,
        eventRegistration,
        emitResponse,
        onSubmit,
        activePaymentMethod,
        ...props
    }) => {
    const {onPaymentSetup, onCheckoutFail} = eventRegistration;
    const {noticeContexts} = emitResponse;
    const stripe = useStripe();
    const [error] = useStripeError();
    const canPay = (result) => result != null && result.applePay;
    const exportedValues = useExportedValues();

    useExpressBreakpointWidth({payment_method: getData('name'), width: 375});

    const {setPaymentMethod} = useProcessPaymentIntent({
        getData,
        billing,
        shippingData,
        onPaymentSetup,
        emitResponse,
        error,
        onSubmit,
        activePaymentMethod,
        exportedValues
    });

    useProcessExpressCheckoutError({
        onError,
        emitResponse,
        onCheckoutFail,
        paymentMethodId: getData('name'),
        activePaymentMethod
    });

    useAfterProcessingPayment({
        getData,
        eventRegistration,
        emitResponse,
        activePaymentMethod,
        messageContext: noticeContexts.EXPRESS_PAYMENTS
    });

    const {paymentRequest} = usePaymentRequest({
        getData,
        onClose,
        stripe,
        billing,
        shippingData,
        setPaymentMethod,
        exportedValues,
        canPay
    });

    const handleClick = useCallback((e) => {
        if (paymentRequest) {
            e.preventDefault();
            onClick();
            paymentRequest.show();
        }
    }, [paymentRequest, onClick]);

    if (paymentRequest) {
        return (
            <button
                className={classnames('apple-pay-button', getData('buttonStyle'), `apple-pay-button-${getData('buttonDesign')}`)}
                style={{
                    'ApplePayButtonType': getData('buttonType')
                }}
                onClick={handleClick}/>

        )
    }
    return null;
}

registerExpressPaymentMethod({
    name: getData('name'),
    canMakePayment: ({cartTotals, ...props}) => {
        if (isCartPage() && !getData('cartCheckoutEnabled')) {
            return false;
        }
        if (isCheckoutPage() && !getData('expressCheckoutEnabled')) {
            return false;
        }
        const {currency_code: currency, total_price} = cartTotals;
        return canMakePayment({
            country: getData('countryCode'),
            currency: currency.toLowerCase(),
            total: {
                label: getData('totalLabel'),
                amount: parseInt(total_price)
            }
        }, (result) => {
            return result != null && result.applePay;
        });
    },
    content: <ApplePayContent getData={getData}/>,
    edit: <ApplePayPreview getData={getData}/>,
    supports: {
        showSavedCards: getData('showSavedCards'),
        showSaveOption: getData('showSaveOption'),
        features: getData('features')
    }
});
